import { Fragment, memo } from "react";
import { DateTime } from "luxon";
import cn from "classnames";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { downloadFile } from "@/store/ses/sesSlice";
import { selectActiveRobotId, selectActiveRobotVersion } from "@/store/ses/sesRobotSlice";
import { changeTimestamp } from "@/store/ses/sesArchiveSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { buttonRegexpPattern, getPostfixWithRobotId, parsingButtonInMessage, splitButtonTemplatesIntoBlocks } from "@/helpers";
import { SES } from "@/constants/accessRights";
import { IMessageProps } from "./Message.props";
import styles from "./Message.module.scss";

const dt = DateTime;

const Message = ({ messageData, idx, isSelectedPair, setSelectedPairOfMessages, service }: IMessageProps): JSX.Element => {
	const dispatch = useAppDispatch();
	const activeRobotId = useAppSelector(selectActiveRobotId); // store - id активного робота
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// обработчик нажатия на ВОП сообщений
	const handleClickOnPairMessages = (idx: number, startRequest: number): void => {
		if (service === "ses") {
			setSelectedPairOfMessages && setSelectedPairOfMessages(idx); // выбор активной пары
			dispatch(changeTimestamp(startRequest)); // изменение timestamp в store
		}
	};

	return (
		<li
			key={messageData.id}
			className={cn(styles.messagePair, {
				[styles.messagePairHover]: service === "ses",
				[styles.messagePairSelected]: service === "ses" && isSelectedPair,
			})}
			onClick={() => handleClickOnPairMessages(idx, messageData.startRequest)}
		>
			{/* сообщение пользователя */}
			<div
				className={cn(styles.messageItem, styles.messageItemClient, {
					[styles.messageItemHidden]: messageData.request?.message === "", // скрыть пустое сообщение
				})}
			>
				<div className={cn(styles.messageText, styles.messageTextClient)}>
					{typeof messageData.request?.message === "string" && messageData.request.message ? messageData.request.message : translate("title_errorMessageNotFound")}
					{messageData.request.datetime && !messageData.request.file && (
						<span className={styles.messageTextTime}>{dt.fromISO(messageData.request.datetime).toFormat("TT")}</span>
					)}
				</div>
			</div>

			{/* файл */}
			{messageData.request.file && (
				<div className={cn(styles.messageItem, styles.messageItemClient)}>
					<div className={cn(styles.messageText, styles.messageTextClient)}>
						{translate("title_file")}:{" "}
						<span
							className={isAccess(SES.DOWNLOAD_FILE) ? styles.messageTextFile : undefined}
							onClick={() => {
								if (messageData.request.file?.file_id) {
									isAccess(SES.DOWNLOAD_FILE) &&
										activeRobotId &&
										dispatch(
											downloadFile({
												robotId: getPostfixWithRobotId(activeRobotVersion, activeRobotId),
												fileId: messageData.request.file.file_id || "",
												fileName: messageData.request.file.filename || "",
											})
										);
								}
							}}
						>
							{messageData.request.file.filename}
						</span>
						{messageData.request.datetime && <span className={styles.messageTextTime}>{dt.fromISO(messageData.request.datetime).toFormat("TT")}</span>}
					</div>
				</div>
			)}

			{/* ответы */}
			{/* доп.проверка на правильный формат данных */}
			{Array.isArray(messageData.reply?.answers) ? (
				messageData.reply.answers.map((replyData, idx, array) => (
					<Fragment key={idx}>
						{/* основные сообщения */}
						{replyData.messages.map((replyMessage) => {
							const buttonsInPhrase = splitButtonTemplatesIntoBlocks(replyMessage); // разбиваем шаблоны кнопок на блоки
							return (
								<div className={styles.messageItem} key={replyMessage}>
									<div className={styles.messageText}>
										{buttonsInPhrase.length > 0 ? (
											<>
												{/* сообщение */}
												{replyMessage.replace(buttonRegexpPattern, "")}
												{/* кнопки */}
												{buttonsInPhrase.map((buttonBlock, blockIdx) => (
													<div className={styles.messageButtonBlock} key={blockIdx}>
														{buttonBlock.map((buttonCode) => {
															const { buttonName } = parsingButtonInMessage(buttonCode); // парсинг кнопки
															return (
																<button className={styles.messageButton} type="button" key={buttonCode} title={buttonName}>
																	{buttonName}
																</button>
															);
														})}
													</div>
												))}
											</>
										) : (
											replyMessage
										)}
										{/* время к последнему сообщению от робота */}
										{messageData.reply.datetime && array.length - 1 === idx && (
											<span className={styles.messageTextTime}>{dt.fromISO(messageData.reply.datetime).toFormat("TT")}</span>
										)}
									</div>
								</div>
							);
						})}
						{/* файл */}
						{replyData.file && (
							<div className={styles.messageItem}>
								<div className={styles.messageText}>
									{translate("title_file")}:{" "}
									<span
										className={isAccess(SES.DOWNLOAD_FILE) ? styles.messageTextFile : undefined}
										onClick={() => {
											if (replyData.file?.file_id) {
												isAccess(SES.DOWNLOAD_FILE) &&
													activeRobotId &&
													dispatch(
														downloadFile({
															robotId: getPostfixWithRobotId(activeRobotVersion, activeRobotId),
															fileId: replyData.file.file_id || "",
															fileName: replyData.file.filename || "",
														})
													);
											}
										}}
									>
										{replyData.file.filename}
									</span>
									{/* время к последнему сообщению от робота */}
									{messageData.reply.datetime && array.length - 1 === idx && (
										<span className={styles.messageTextTime}>{dt.fromISO(messageData.reply.datetime).toFormat("TT")}</span>
									)}
								</div>
							</div>
						)}
						{/* перевод диалога */}
						{replyData.transfer && (
							<div className={styles.messageItem}>
								<div className={styles.messageText}>
									{translate("title_transferDialogue")}: {replyData.transfer}
									{/* время к последнему сообщению от робота */}
									{messageData.reply.datetime && array.length - 1 === idx && (
										<span className={styles.messageTextTime}>{dt.fromISO(messageData.reply.datetime).toFormat("TT")}</span>
									)}
								</div>
							</div>
						)}
					</Fragment>
				))
			) : (
				<div className={styles.messageItem}>
					<div className={styles.messageText}>{`<${translate("title_dataFormatError")}>`}</div>
				</div>
			)}
		</li>
	);
};

export default memo(Message);
